// Product analytics: one call, two destinations.
//  - our own API (/api/events), which we can query for funnels and attribution;
//  - Firebase / GA4 (logEvent), for the Google Analytics dashboards.
// No cookies and no personal data: a random visitor id in localStorage, plus first-touch
// attribution (utm_*, referrer, landing page) so each signup knows where it came from.
import firebase from 'firebase/compat/app';
import 'firebase/compat/analytics';
import { PORTAL } from '../portal';

const base = (process.env.REACT_APP_API_URL || 'https://football.olympikesoft.com').replace(/\/+$/, '');
const ENDPOINT = `${base}/api/events`;
const VISITOR_KEY = 'mfg_vid';
const ATTRIBUTION_KEY = 'mfg_first_touch';
const FLUSH_MS = 4000;

const store = {
  get(k) { try { return localStorage.getItem(k); } catch { return null; } },
  set(k, v) { try { localStorage.setItem(k, v); } catch { /* private mode */ } },
};

function visitorId() {
  let id = store.get(VISITOR_KEY);
  if (!id) {
    id = (window.crypto?.randomUUID?.() || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`);
    store.set(VISITOR_KEY, id);
  }
  return id;
}

// First touch wins: the visit that brought this person to the site for the first time.
function captureAttribution() {
  const existing = store.get(ATTRIBUTION_KEY);
  if (existing) { try { return JSON.parse(existing); } catch { /* rewrite below */ } }
  const params = new URLSearchParams(window.location.search);
  const ref = document.referrer && !document.referrer.includes(window.location.host) ? document.referrer : '';
  const a = { landing_path: window.location.pathname, first_seen: new Date().toISOString() };
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach((k) => { if (params.get(k)) a[k] = params.get(k); });
  if (ref) { try { a.referrer = new URL(ref).hostname; } catch { /* ignore */ } }
  // Challenge links and portal builds carry no utm tags: the link / portal itself is the source.
  if (!a.utm_source && PORTAL) { a.utm_source = PORTAL; a.utm_medium = 'portal'; }
  if (!a.utm_source && window.location.pathname.startsWith('/c/')) { a.utm_source = 'challenge_link'; a.utm_medium = 'share'; }
  store.set(ATTRIBUTION_KEY, JSON.stringify(a));
  return a;
}

let queue = [];
let timer = null;
let attribution = null;

function flush(useBeacon = false) {
  if (!queue.length) return;
  const events = queue.splice(0, 25);
  const body = JSON.stringify({ visitorId: visitorId(), attribution, events });
  const token = store.get('token');
  // A signed-in request needs the Authorization header, which sendBeacon cannot set.
  if (useBeacon && !token && navigator.sendBeacon) {
    navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }));
  } else {
    fetch(ENDPOINT, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token.replace(/^"|"$/g, '')}` }) },
      body,
    }).catch(() => {});
  }
  if (queue.length) flush(useBeacon);
}

let ga = null;
function gaLog(name, props) {
  try {
    if (!ga && firebase.apps.length) ga = firebase.analytics();
    ga?.logEvent(name, props);
  } catch { /* analytics blocked */ }
}

/** Record a product event. Props must be small and contain no personal data. */
export function track(name, props = {}) {
  if (typeof window === 'undefined') return;
  if (!attribution) attribution = captureAttribution();
  queue.push({ name, path: window.location.pathname, props });
  gaLog(name, props);
  if (!timer) timer = setTimeout(() => { timer = null; flush(); }, FLUSH_MS);
  if (queue.length >= 20) flush();
}

/** Send immediately (e.g. right before a navigation that leaves the page). */
export function trackNow(name, props = {}) {
  track(name, props);
  clearTimeout(timer); timer = null;
  flush();
}

let installed = false;
/** Page views, JS errors and flushing when the tab is hidden. Call once at startup. */
export function installTracking() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  attribution = captureAttribution();

  // One per page load: where this visit came from (first touch is kept separately for signups).
  const params = new URLSearchParams(window.location.search);
  const ref = document.referrer && !document.referrer.includes(window.location.host) ? document.referrer : '';
  let refHost = '';
  try { refHost = ref ? new URL(ref).hostname : ''; } catch { /* ignore */ }
  track('visit', {
    utm_source: params.get('utm_source') || PORTAL || (window.location.pathname.startsWith('/c/') ? 'challenge_link' : null),
    utm_campaign: params.get('utm_campaign'),
    utm_content: params.get('utm_content'),
    referrer: refHost || null,
    landing_path: window.location.pathname,
    returning: !!attribution?.first_seen && attribution.first_seen.slice(0, 10) !== new Date().toISOString().slice(0, 10),
    mobile: window.matchMedia?.('(max-width: 767px)').matches || false,
    lang: navigator.language?.slice(0, 5) || null,
  });

  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flush(true); });
  window.addEventListener('pagehide', () => flush(true));

  let errors = 0;
  window.addEventListener('error', (e) => {
    if (++errors > 5) return;
    // Minified errors often arrive as just "Uncaught": keep the thrown value and the top of the stack.
    const err = e.error;
    track('js_error', {
      message: String(err?.message || e.message || '').slice(0, 200),
      type: err?.name || typeof err,
      stack: String(err?.stack || '').split('\n').slice(0, 4).join(' | ').slice(0, 400),
      source: String(e.filename || '').split('/').pop().slice(0, 80), line: e.lineno || 0, col: e.colno || 0,
    });
  });
  window.addEventListener('unhandledrejection', (e) => {
    if (++errors > 5) return;
    track('js_error', { message: String(e.reason?.message || e.reason || '').slice(0, 200), kind: 'promise', stack: String(e.reason?.stack || '').split('\n').slice(0, 4).join(' | ').slice(0, 400) });
  });
}
